import React, { useState } from 'react';
import { MMGroup, MMMode } from '../../api/mmode';
import { batchUpdate } from '../../api/batch';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import Modal from '../Modal';
import SearchSelect from '../SearchSelect';
import { alertDialog } from '../../utils/dialog';
import { zhOptions } from '../../utils/searchOptions';

// 模式值迁移 (2026-11-01): 把勾选的多个模式值一次改到另一个加工模式组 (= 归并).
// 与编辑弹窗里改"加工模式组"同一语义, 只是批量 —— 走 /v1/batch/update, N 个模式值一张工单,
// 回放仍是 mm_mode 单条 update, 全成全败. 已在目标组里的自动跳过, 不进工单.

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

export interface MMModeMigrateModalProps {
    /** 勾选的模式值 */
    modes: MMMode[];
    groups: MMGroup[];
    /** 默认目标组 (缺省不选) */
    defaultGroup?: string;
    onClose: () => void;
    /** 提交成功后回调 (调用方清勾选 + 刷新) */
    onDone: () => void | Promise<void>;
}

function MMModeMigrateModal({
    modes,
    groups,
    defaultGroup = '',
    onClose,
    onDone,
}: MMModeMigrateModalProps) {
    const [target, setTarget] = useState(defaultGroup);
    const [busy, setBusy] = useState(false);

    const groupName = (code: string) =>
        groups.find((g) => g.code === code)?.nameZh || code;
    const moving = target ? modes.filter((m) => m.groupCode !== target) : [];
    const skipped = target ? modes.length - moving.length : 0;

    const submit = async () => {
        if (!target) {
            alertDialog('请选择目标加工模式');
            return;
        }
        if (moving.length === 0) {
            alertDialog('所选模式值都已在目标加工模式下, 无需迁移');
            return;
        }
        setBusy(true);
        try {
            const res = await batchUpdate(
                'mm_mode',
                moving.map((m) => ({
                    targetCode: m.value,
                    data: { value: m.value, groupCode: target },
                })),
            );
            await onDone();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`迁移失败: ${errMsg(e)}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open title={`迁移模式值 (${modes.length})`} onClose={onClose}>
            <div className="space-y-3">
                <div className="text-xs text-slate-500">
                    把所选模式值归到另一个加工模式 (复用组) 下: 迁移后它们按目标组解析参数,
                    与目标组内其他模式值共享一份参数.
                </div>
                <div>
                    <label className="field-label">目标加工模式</label>
                    <SearchSelect
                        className="w-full"
                        value={target}
                        onChange={setTarget}
                        placeholder="选择加工模式…"
                        options={zhOptions(
                            groups,
                            (g) => g.code,
                            (g) => g.nameZh,
                        )}
                    />
                </div>
                <div className="max-h-64 overflow-auto rounded border border-slate-100">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-xs text-slate-500 text-left bg-slate-50">
                                <th className="px-3 py-1.5">模式值</th>
                                <th>中文名</th>
                                <th>当前加工模式</th>
                                <th className="pr-3 text-right">迁移后</th>
                            </tr>
                        </thead>
                        <tbody>
                            {modes.map((m) => {
                                const same = !!target && m.groupCode === target;
                                return (
                                    <tr
                                        key={m.value}
                                        className="border-t border-slate-100"
                                    >
                                        <td className="px-3 py-1.5 font-mono text-xs">
                                            {m.value}
                                        </td>
                                        <td>{m.nameZh || '—'}</td>
                                        <td className="text-xs text-slate-500">
                                            {groupName(m.groupCode)}
                                        </td>
                                        <td className="pr-3 text-right text-xs">
                                            {!target ? (
                                                <span className="text-slate-300">—</span>
                                            ) : same ? (
                                                <span className="text-slate-400">
                                                    已在该组, 跳过
                                                </span>
                                            ) : (
                                                <span className="text-brand-600">
                                                    → {groupName(target)}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {skipped > 0 && (
                    <div className="text-xs text-slate-400">
                        已跳过 {skipped} 个已在目标组的模式值
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="btn-ghost" onClick={onClose}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={submit}
                        disabled={busy || moving.length === 0}
                    >
                        {busy
                            ? '提交中…'
                            : `迁移${moving.length ? ` (${moving.length})` : ''}`}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default MMModeMigrateModal;
